import type { PageExperience } from '@/features/experiences/types';
import {
  AddToCalendarConfigAccess,
  AddToCalendarConfigKeys,
  InteractionAppNames,
  Role,
} from '@/types/gql.generated';

const isAccessValue = (value: string): value is AddToCalendarConfigAccess =>
  (Object.values(AddToCalendarConfigAccess) as string[]).includes(value);

/**
 * The audience the experience offers Add to Calendar to, or null when it has
 * none: an app installed outside a planner, or one whose config is assembled
 * without the setting (the magic preview builds its own). Null is not "no one" —
 * it is the behavior that predates the setting, so callers treat it as
 * unrestricted.
 */
export const getExperienceAddToCalendarAccess = (
  experience: PageExperience | undefined
): AddToCalendarConfigAccess | null => {
  const value = experience?.interactionApps
    .find((app) => app.applicationName === InteractionAppNames.AddToCalendar)
    ?.config.find(
      (config) => config.setting === AddToCalendarConfigKeys.Access
    )?.value;

  return value && isAccessValue(value) ? value : null;
};

// The roles each audience admits. Role.Public is what the server resolves for a
// caller holding no grant on the experience — a visitor — so it appears under
// PUBLIC alone. A collaborator is anyone with an explicit permission whatever
// its level, which is how the share modal lists them.
const ROLES_BY_ACCESS: Record<AddToCalendarConfigAccess, Role[]> = {
  [AddToCalendarConfigAccess.Off]: [],
  [AddToCalendarConfigAccess.Owner]: [Role.Owner],
  [AddToCalendarConfigAccess.Collaborator]: [
    Role.Owner,
    Role.Editor,
    Role.Viewer,
  ],
  [AddToCalendarConfigAccess.Public]: [
    Role.Owner,
    Role.Editor,
    Role.Viewer,
    Role.Public,
  ],
};

/**
 * Whether a viewer in `role` is inside the chosen audience.
 *
 * A null `access` is an experience that never configured one — the interaction
 * is installed well outside planners, so withholding it there would take a
 * working button away from pages this setting was never about.
 */
export const roleHasAddToCalendarAccess = (
  access: AddToCalendarConfigAccess | null,
  role: Role | undefined
): boolean => {
  if (access === null) {
    return true;
  }
  return role != null && ROLES_BY_ACCESS[access].includes(role);
};
